/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useDebounce } from "../../hooks/useDebounce";
import {
  IEsimPackage,
  ILocationZone
} from "../../interfaces/esim/EsimPackages.types";
import {
  getLocationZones,
  getPackageById,
  getPackagesByLocationZone
} from "../../services/Esim.service";
import { SelectOption } from "../ui/input/Input.types";
import SelectInput from "../ui/input/SelectInput";

interface EsimPackageSelectorProps {
  value: number | null | undefined;
  onChange: (
    packageId: number | undefined,
    pkg: IEsimPackage | null
  ) => void;
  error?: string;
}

// Zones are loaded a page at a time and grown on scroll instead of all at once.
const ZONE_PAGE_SIZE = 10;

const formatGB = (gb: number, isUnlimited: boolean) => {
  if (isUnlimited) return "Unlimited";
  return gb >= 1 ? `${gb} GB` : `${Math.round(gb * 1024)} MB`;
};

const EsimPackageSelector: React.FC<EsimPackageSelectorProps> = ({
  value,
  onChange,
  error
}) => {
  // --- Location zones (server-side search + infinite scroll) ---
  const [zones, setZones] = useState<ILocationZone[]>([]);
  const [zoneSearch, setZoneSearch] = useState("");
  const debouncedZoneSearch = useDebounce(zoneSearch, 300);
  const [zonePage, setZonePage] = useState(1);
  const [zoneTotalPages, setZoneTotalPages] = useState(1);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zonesLoadingMore, setZonesLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const [selectedZoneSlug, setSelectedZoneSlug] = useState<string | null>(null);
  // Kept separately so the picked/edit-mode zone keeps showing even when it
  // isn't part of the currently-loaded page of options.
  const [selectedZoneOption, setSelectedZoneOption] =
    useState<SelectOption | null>(null);

  const [packages, setPackages] = useState<IEsimPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const initializedRef = useRef(false);

  // Load the first page whenever the (debounced) search term changes.
  useEffect(() => {
    let cancelled = false;
    const fetchFirstPage = async () => {
      setZonesLoading(true);
      try {
        const res = await getLocationZones(
          debouncedZoneSearch,
          1,
          ZONE_PAGE_SIZE
        );
        if (cancelled) return;
        if (res.data.succeeded) {
          setZones(res.data.data);
          setZonePage(1);
          setZoneTotalPages(res.data.totalPages ?? 1);
        }
      } catch {
        if (!cancelled) toast.error("Gabim gjate ngarkimit te zonave.");
      } finally {
        if (!cancelled) setZonesLoading(false);
      }
    };
    void fetchFirstPage();
    return () => {
      cancelled = true;
    };
  }, [debouncedZoneSearch]);

  // Append the next page when the dropdown is scrolled to the bottom.
  const loadMoreZones = useCallback(() => {
    if (loadingMoreRef.current || zonesLoading) return;
    if (zonePage >= zoneTotalPages) return;

    loadingMoreRef.current = true;
    const nextPage = zonePage + 1;
    setZonesLoadingMore(true);

    getLocationZones(debouncedZoneSearch, nextPage, ZONE_PAGE_SIZE)
      .then((res) => {
        if (res.data.succeeded) {
          setZones((prev) => {
            const seen = new Set(prev.map((z) => z.slug));
            const incoming = res.data.data.filter((z) => !seen.has(z.slug));
            return [...prev, ...incoming];
          });
          setZonePage(nextPage);
          setZoneTotalPages(res.data.totalPages ?? zoneTotalPages);
        }
      })
      .catch(() => toast.error("Gabim gjate ngarkimit te zonave."))
      .finally(() => {
        loadingMoreRef.current = false;
        setZonesLoadingMore(false);
      });
  }, [zonesLoading, zonePage, zoneTotalPages, debouncedZoneSearch]);

  // Edit mode: resolve the package's zone + sibling packages from `value`.
  useEffect(() => {
    if (!value || initializedRef.current) return;
    initializedRef.current = true;

    const resolve = async () => {
      setInitializing(true);
      try {
        const pkgRes = await getPackageById(value);
        const pkg = pkgRes.data;
        if (!pkg) return;

        setSelectedZoneSlug(pkg.locationZoneSlug);
        // The zone may not be in the first page, so seed a display option.
        // For country-level zones the zone name equals the single country name.
        const zoneName =
          pkg.countries && pkg.countries.length === 1
            ? pkg.countries[0].name
            : "";
        setSelectedZoneOption({
          id: pkg.locationZoneSlug,
          name: zoneName,
          imageUrl: pkg.imageUrl
        });

        const siblingsRes = await getPackagesByLocationZone(
          pkg.locationZoneSlug
        );
        if (siblingsRes.data) {
          setPackages(siblingsRes.data);
        }
      } catch {
        // Silent — user can still pick manually.
      } finally {
        setInitializing(false);
      }
    };
    void resolve();
  }, [value]);

  useEffect(() => {
    if (initializing || !selectedZoneSlug) return;

    let cancelled = false;
    const fetchPackages = async () => {
      setPackagesLoading(true);
      try {
        const res = await getPackagesByLocationZone(selectedZoneSlug);
        if (cancelled) return;
        if (res.data) {
          setPackages(res.data);
        }
      } catch {
        if (!cancelled) toast.error("Gabim gjate ngarkimit te paketave.");
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    };
    void fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [selectedZoneSlug, initializing]);

  const zoneOptions = useMemo<SelectOption[]>(
    () =>
      zones.map((z) => ({
        id: z.slug,
        name: z.name,
        imageUrl: z.imageUrl
      })),
    [zones]
  );

  const packageOptions = useMemo<SelectOption[]>(
    () =>
      packages.map((p) => ({
        id: p.id,
        name: `${p.name} — ${formatGB(p.totalGB, p.isUnlimited)}, ${
          p.validityDays
        } ditë, ${p.cost} EUR`,
        imageUrl: p.imageUrl
      })),
    [packages]
  );

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === value) ?? null,
    [packages, value]
  );

  const handleZoneSelect = (opt: SelectOption | null) => {
    setSelectedZoneSlug(opt ? String(opt.id) : null);
    setSelectedZoneOption(opt);
    setPackages([]);
    if (selectedPackage) {
      onChange(undefined, null);
    }
  };

  const handlePackageSelect = (opt: SelectOption | null) => {
    if (!opt) return;
    const pkg = packages.find((p) => p.id === opt.id);
    if (pkg) onChange(pkg.id, pkg);
  };

  return (
    <div className="mb-6">
      <SelectInput
        label="Zona e lokacionit"
        placeholder="Zgjidh zonën"
        options={zoneOptions}
        selectedId={selectedZoneSlug ?? ""}
        selectedItem={selectedZoneOption}
        loading={zonesLoading || initializing}
        loadingMore={zonesLoadingMore}
        serverSideSearch
        onSearchChange={setZoneSearch}
        onLoadMore={loadMoreZones}
        onOptionSelect={handleZoneSelect}
      />

      {selectedZoneSlug && (
        <div className="mt-4">
          <SelectInput
            label="Paketa eSIM"
            placeholder={
              packagesLoading ? "Duke ngarkuar paketat..." : "Zgjidh paketën"
            }
            options={packageOptions}
            selectedId={value ?? ""}
            loading={packagesLoading}
            error={error}
            onOptionSelect={handlePackageSelect}
          />
        </div>
      )}

      {selectedPackage && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs text-green-700">
            Paketa e zgjedhur: <strong>{selectedPackage.name}</strong> —{" "}
            {formatGB(selectedPackage.totalGB, selectedPackage.isUnlimited)},{" "}
            {selectedPackage.validityDays} ditë, {selectedPackage.cost} EUR
          </p>
        </div>
      )}
    </div>
  );
};

export default EsimPackageSelector;
